/**
 * Layout Tokens - Sistema de Diseño UNMSM
 *
 * Tokens relacionados con el layout: bordes, sombras, z-index, opacidades,
 * breakpoints, tamaños de iconos y anchos máximos.
 *
 * @module LayoutTokens
 */

/**
 * Sistema de bordes: radios, anchos y estilos
 */
export const BORDERS = {
  /**
   * Radios de borde (border-radius)
   * Vuetify usa: rounded-0, rounded, rounded-sm, rounded-lg, rounded-xl, rounded-pill, rounded-circle
   */
  radius: {
    /** 0px - Sin redondeo */
    none: '0',
    /** 4px - Redondeo pequeño */
    sm: '0.25rem',
    /** 8px - Redondeo medio (base de Vuetify) */
    md: '0.5rem',
    /** 12px - Redondeo grande */
    lg: '0.75rem',
    /** 16px - Redondeo extra grande */
    xl: '1rem',
    /** 24px - Redondeo 2xl */
    '2xl': '1.5rem',
    /** 9999px - Completamente redondeado (píldora) */
    full: '9999px',
    /** 50% - Círculo perfecto */
    circle: '50%',
  },

  /**
   * Anchos de borde
   */
  width: {
    /** 0px - Sin borde */
    none: '0',
    /** 1px - Borde fino (por defecto) */
    thin: '1px',
    /** 2px - Borde medio */
    medium: '2px',
    /** 4px - Borde grueso */
    thick: '4px',
  },

  /**
   * Estilos de borde
   */
  style: {
    solid: 'solid',
    dashed: 'dashed',
    dotted: 'dotted',
    double: 'double',
    none: 'none',
  },
} as const

/**
 * Sistema de elevaciones basado en Material Design
 * Sombras suaves y coherentes para profundidad visual
 *
 * @example
 * ```typescript
 * box-shadow: SHADOWS.md;
 * ```
 */
export const SHADOWS = {
  /** Sin sombra */
  none: 'none',

  /** Elevación 1 - Elementos sutiles (cards planas) */
  xs: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',

  /** Elevación 2 - Cards, botones elevados */
  sm: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px -1px rgba(0, 0, 0, 0.1)',

  /** Elevación 4 - Dropdowns, menús pequeños */
  md: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1)',

  /** Elevación 8 - Menús, popovers */
  lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -4px rgba(0, 0, 0, 0.1)',

  /** Elevación 12 - Diálogos, modales */
  xl: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)',

  /** Elevación 16 - Elementos flotantes principales */
  '2xl': '0 25px 50px -12px rgba(0, 0, 0, 0.25)',

  /** Sombra interior */
  inner: 'inset 0 2px 4px 0 rgba(0, 0, 0, 0.05)',
} as const

/**
 * Breakpoints para diseño responsive
 * Alineados con Vuetify Display breakpoints
 *
 * @see https://vuetifyjs.com/en/features/display-and-platform/
 *
 * @example
 * ```typescript
 * // En media queries
 * @media (min-width: ${BREAKPOINTS.md}) {
 *   // Estilos para tablets y superiores
 * }
 * ```
 */
export const BREAKPOINTS = {
  /** 0px - Extra small (móviles) */
  xs: 0,
  /** 600px - Small (móviles grandes / tablets pequeñas) */
  sm: 600,
  /** 960px - Medium (tablets) */
  md: 960,
  /** 1280px - Large (laptops) */
  lg: 1280,
  /** 1920px - Extra large (desktops) */
  xl: 1920,
  /** 2560px - Extra extra large (pantallas 4K) */
  xxl: 2560,
} as const

/**
 * Breakpoints como strings con unidades (útil para media queries)
 */
export const BREAKPOINTS_PX = {
  xs: `${BREAKPOINTS.xs}px`,
  sm: `${BREAKPOINTS.sm}px`,
  md: `${BREAKPOINTS.md}px`,
  lg: `${BREAKPOINTS.lg}px`,
  xl: `${BREAKPOINTS.xl}px`,
  xxl: `${BREAKPOINTS.xxl}px`,
} as const

/**
 * Escala de z-index para gestionar capas de elementos
 * Evita conflictos y mantiene jerarquía visual clara
 *
 * @example
 * ```typescript
 * z-index: Z_INDEX.dropdown;
 * ```
 */
export const Z_INDEX = {
  /** -1 - Elementos detrás del contenido */
  behind: -1,
  /** 0 - Contenido normal */
  base: 0,
  /** 10 - Elementos ligeramente elevados (sticky headers) */
  sticky: 10,
  /** 50 - Dropdowns, menús desplegables */
  dropdown: 50,
  /** 100 - Overlays, fondos de modales */
  overlay: 100,
  /** 200 - Modales, diálogos */
  modal: 200,
  /** 300 - Popovers, tooltips */
  popover: 300,
  /** 400 - Notificaciones, toasts */
  notification: 400,
  /** 9999 - Elementos que deben estar siempre encima */
  maximum: 9999,
} as const

/**
 * Valores de opacidad consistentes
 * Basados en Material Design opacity levels
 */
export const OPACITY = {
  /** 0 - Invisible */
  invisible: 0,
  /** 0.04 - Idle state */
  idle: 0.04,
  /** 0.08 - Hover state (superficies) */
  hover: 0.08,
  /** 0.12 - Focus state */
  focus: 0.12,
  /** 0.38 - Disabled */
  disabled: 0.38,
  /** 0.60 - Medium emphasis (texto secundario) */
  medium: 0.60,
  /** 0.87 - High emphasis (texto principal) */
  high: 0.87,
  /** 1 - Completamente opaco */
  opaque: 1,
} as const

/**
 * Tamaños estándar para iconos
 * Alineados con Material Design Icons
 */
export const ICON_SIZES = {
  /** 16px - Íconos extra pequeños (inline en texto) */
  xs: '1rem',
  /** 20px - Íconos pequeños */
  sm: '1.25rem',
  /** 24px - Íconos medianos (tamaño base de MDI) */
  md: '1.5rem',
  /** 32px - Íconos grandes */
  lg: '2rem',
  /** 48px - Íconos extra grandes */
  xl: '3rem',
  /** 64px - Íconos 2xl */
  '2xl': '4rem',
} as const

/**
 * Anchos máximos para contenedores
 * Controlan el ancho máximo del contenido en diferentes contextos
 */
export const MAX_WIDTH = {
  /** 320px - Elementos muy pequeños (mobile) */
  xs: '20rem',
  /** 448px - Elementos pequeños */
  sm: '28rem',
  /** 672px - Contenido mediano (formularios) */
  md: '42rem',
  /** 896px - Contenido grande */
  lg: '56rem',
  /** 1120px - Contenido extra grande */
  xl: '70rem',
  /** 1344px - Contenido 2xl */
  '2xl': '84rem',
  /** 1536px - Contenido 3xl */
  '3xl': '96rem',
  /** 1728px - Contenido 4xl */
  '4xl': '108rem',
  /** 100% - Sin restricción de ancho */
  full: '100%',
  /** min-content - Ancho mínimo del contenido */
  min: 'min-content',
  /** max-content - Ancho máximo del contenido */
  max: 'max-content',
  /** fit-content - Ajuste al contenido */
  fit: 'fit-content',
} as const

/**
 * Tipo para radios de borde
 */
export type BorderRadius = keyof typeof BORDERS.radius

/**
 * Tipo para anchos de borde
 */
export type BorderWidth = keyof typeof BORDERS.width

/**
 * Tipo para sombras
 */
export type Shadow = keyof typeof SHADOWS

/**
 * Tipo para breakpoints
 */
export type Breakpoint = keyof typeof BREAKPOINTS

/**
 * Tipo para valores de z-index
 */
export type ZIndex = keyof typeof Z_INDEX

/**
 * Tipo para valores de opacidad
 */
export type Opacity = keyof typeof OPACITY

/**
 * Tipo para tamaños de iconos
 */
export type IconSize = keyof typeof ICON_SIZES

/**
 * Tipo para anchos máximos
 */
export type MaxWidth = keyof typeof MAX_WIDTH
